// Apolo IDE — web_act: the agent ACTS on the live web through the user's own
// logged-in Chrome (chrome-devtools-mcp). The write-side companion to
// web_browse (which only READS): web_act clicks, types, fills forms, uploads
// files, presses keys and navigates — everything needed to complete a task on
// a real site (apply to a job, submit a form) from inside the user's session.
//
// HOW IT DRIVES CHROME: every action goes through window.apolo.browser.chromeMcp
// (the chrome-devtools-mcp daemon). Elements are addressed by `uid`, which the
// model gets from a `snapshot` action (the accessibility tree). The canonical
// loop is: snapshot → read uids → click/fill/upload by uid → snapshot again to
// confirm. Interaction actions return the fresh snapshot (includeSnapshot) so
// the model sees the resulting page without a second call.
//
// SAFETY: this is the user's REAL browser and session — same trust as the human
// clicking. It is `mutating: true`, so Team Mode's read-only workers never get
// it and the loop can gate it under permissionsMode 'ask'. Hermes runs
// autonomously (permissionsMode 'ignore'), which is the whole point here. The
// tool NEVER hard-fails: any miss is a clear string the model can recover from.
//
// REQUIRES the chrome-mcp bridge (the .app on the user's Mac). Without it —
// e.g. a cloud run with no Chrome — every action returns a clear error telling
// the model the logged-in browser is unavailable.

import { mcpCallFailed, contextualMcpCall } from './mcpCallResult.js';
import { isWebUrl } from './browserReadPlan.js';

const MAX_CHARS = 18000;

export const WEB_ACT_DEF = {
  type: 'function',
  function: {
    name: 'web_act',
    description:
      'Atua na web pelo Chrome REAL logado do usuário (clicar, digitar, preencher, anexar arquivo, navegar) — use para COMPLETAR uma tarefa num site real (ex: aplicar numa vaga, enviar um formulário). ' +
      'FLUXO: 1) action="snapshot" para ver a página e pegar os uid dos elementos; 2) aja por uid (click/fill/upload_file/...); 3) os passos de ação já devolvem o snapshot novo para você confirmar. ' +
      'actions: snapshot (lê a árvore com uids) · navigate {url} · click {uid, dblClick?} · fill {uid, value} (input/textarea/select) · fill_form {elements:[{uid,value}]} · type {text, submitKey?} (no campo focado) · press_key {key} (ex "Enter","Tab","Control+A") · upload_file {uid, filePath} (anexar arquivo a um <input type=file>) · hover {uid} · select {uid, value} · wait_for {text} · screenshot {fullPage?} · evaluate {function}. ' +
      'Pré-requisito: roda no app do usuário, no Mac, com o Chrome logado. Ex: {"action":"snapshot"} depois {"action":"upload_file","uid":"42","filePath":"/Users/.../cv.pdf"}.',
    parameters: {
      type: 'object',
      properties: {
        action: {
          type: 'string',
          enum: [
            'snapshot',
            'navigate',
            'click',
            'fill',
            'fill_form',
            'type',
            'press_key',
            'upload_file',
            'hover',
            'select',
            'wait_for',
            'screenshot',
            'evaluate',
          ],
          description: 'o que fazer (snapshot primeiro para obter os uids)',
        },
        url: { type: 'string', description: 'navigate: a página a abrir' },
        uid: { type: 'string', description: 'click/fill/upload_file/hover/select: o uid do elemento (do snapshot)' },
        value: { type: 'string', description: 'fill/select: o valor a inserir/escolher' },
        text: { type: 'string', description: 'type: o texto a digitar · wait_for: o texto a aguardar' },
        key: { type: 'string', description: 'press_key: a tecla (ex "Enter", "Tab", "Control+A")' },
        filePath: { type: 'string', description: 'upload_file: caminho absoluto do arquivo a anexar' },
        elements: {
          type: 'array',
          description: 'fill_form: lista de {uid, value} a preencher de uma vez',
          items: {
            type: 'object',
            properties: { uid: { type: 'string' }, value: { type: 'string' } },
          },
        },
        submitKey: { type: 'string', description: 'type: tecla opcional a apertar depois (ex "Enter")' },
        dblClick: { type: 'boolean', description: 'click: clique duplo' },
        fullPage: { type: 'boolean', description: 'screenshot: capturar a página inteira' },
        timeout: { type: 'number', description: 'wait_for: timeout em ms' },
        func: { type: 'string', description: 'evaluate: corpo da função JS a executar (string)' },
      },
      required: ['action'],
    },
  },
};

// Flatten an MCP tools/call result into text. chrome-devtools-mcp returns
// { content:[{type:'text',text}], isError? }; older shapes may carry .text or
// .snapshot. Mirrors webBrowseTool's flattener so behavior is consistent.
function textFromMcpResult(result) {
  if (result == null) return '';
  if (typeof result === 'string') return result;
  if (Array.isArray(result.content)) {
    return result.content
      .map((c) => (typeof c === 'string' ? c : c && typeof c.text === 'string' ? c.text : ''))
      .filter(Boolean)
      .join('\n');
  }
  if (typeof result.text === 'string') return result.text;
  if (typeof result.snapshot === 'string') return result.snapshot;
  try {
    return JSON.stringify(result);
  } catch {
    return '';
  }
}

function cap(text) {
  const s = String(text == null ? '' : text);
  if (s.length <= MAX_CHARS) return s;
  return `${s.slice(0, MAX_CHARS)}\n[…truncado em ${MAX_CHARS} chars de ${s.length}]`;
}

// Map a web_act action to the chrome-devtools-mcp { method, params } pair.
// Returns { method, params } or { error } when required args are missing.
// `includeSnapshot:true` on the interaction actions makes Chrome return the
// fresh accessibility tree in the same round-trip.
function planCall(args) {
  const action = args.action;
  const uid = typeof args.uid === 'string' ? args.uid : args.uid != null ? String(args.uid) : undefined;
  switch (action) {
    case 'snapshot':
      return { method: 'take_snapshot', params: {} };
    case 'navigate': {
      const url = typeof args.url === 'string' ? args.url.trim() : '';
      if (!url) return { error: 'navigate exige url' };
      if (!isWebUrl(url)) return { error: 'navigate exige endereço HTTP ou HTTPS' };
      return { method: 'navigate_page', params: { url } };
    }
    case 'click':
      if (!uid) return { error: 'click exige uid (rode snapshot primeiro)' };
      return { method: 'click', params: { uid, dblClick: !!args.dblClick, includeSnapshot: true } };
    case 'hover':
      if (!uid) return { error: 'hover exige uid' };
      return { method: 'hover', params: { uid, includeSnapshot: true } };
    case 'fill':
      if (!uid) return { error: 'fill exige uid' };
      return { method: 'fill', params: { uid, value: String(args.value ?? ''), includeSnapshot: true } };
    case 'select':
      if (!uid) return { error: 'select exige uid' };
      return { method: 'fill', params: { uid, value: String(args.value ?? ''), includeSnapshot: true } };
    case 'fill_form': {
      const elements = Array.isArray(args.elements)
        ? args.elements
            .map((e) => ({ uid: e?.uid != null ? String(e.uid) : '', value: String(e?.value ?? '') }))
            .filter((e) => e.uid)
        : [];
      if (!elements.length) return { error: 'fill_form exige elements:[{uid,value}]' };
      return { method: 'fill_form', params: { elements, includeSnapshot: true } };
    }
    case 'type': {
      const text = typeof args.text === 'string' ? args.text : '';
      if (!text) return { error: 'type exige text' };
      const params = { text };
      if (typeof args.submitKey === 'string' && args.submitKey) params.submitKey = args.submitKey;
      return { method: 'type_text', params };
    }
    case 'press_key': {
      const key = typeof args.key === 'string' ? args.key.trim() : '';
      if (!key) return { error: 'press_key exige key (ex "Enter")' };
      return { method: 'press_key', params: { key, includeSnapshot: true } };
    }
    case 'upload_file': {
      if (!uid) return { error: 'upload_file exige uid (do <input type=file>)' };
      const filePath = typeof args.filePath === 'string' ? args.filePath.trim() : '';
      if (!filePath) return { error: 'upload_file exige filePath absoluto' };
      return { method: 'upload_file', params: { uid, filePath, includeSnapshot: true } };
    }
    case 'wait_for': {
      const text = typeof args.text === 'string' ? args.text.trim() : '';
      if (!text) return { error: 'wait_for exige text' };
      const params = { text: [text] };
      if (Number.isFinite(args.timeout)) params.timeout = args.timeout;
      return { method: 'wait_for', params };
    }
    case 'screenshot':
      return { method: 'take_screenshot', params: { fullPage: !!args.fullPage } };
    case 'evaluate': {
      const fn = typeof args.func === 'string' ? args.func : typeof args.function === 'string' ? args.function : '';
      if (!fn) return { error: 'evaluate exige func (corpo da função JS)' };
      return { method: 'evaluate_script', params: { function: fn } };
    }
    default:
      return { error: `action desconhecida: ${action}` };
  }
}

/**
 * Build the web_act extra-tool for the agentLoop seam.
 * @param {{ apolo?: object }} deps  injectable renderer bridge (defaults to window.apolo)
 * @returns {{ def: object, execute: (args:object) => Promise<string>, mutating: true }}
 */
export function makeWebActTool({ apolo } = {}) {
  let contextVersion;
  const resolveApolo = () =>
    apolo || (typeof window !== 'undefined' ? window.apolo : undefined) || undefined;

  async function execute(args = {}) {
    try {
      const action = typeof args.action === 'string' ? args.action.trim() : '';
      if (!action) return 'Erro web_act: informe action (ex "snapshot").';

      const a = resolveApolo();
      const bridge = a && a.browser && a.browser.chromeMcp;
      if (!bridge || typeof bridge.call !== 'function') {
        return 'Erro web_act: Chrome logado indisponível — web_act precisa do app rodando no Mac do usuário (a ponte chrome-mcp não está disponível neste ambiente).';
      }

      const plan = planCall(args);
      if (plan.error) return `Erro web_act: ${plan.error}`;

      const expected = ['snapshot', 'navigate'].includes(action) ? undefined : contextVersion;
      const res = await bridge.call(contextualMcpCall({ method: plan.method, params: plan.params }, expected));
      if (mcpCallFailed(res)) {
        return `Erro web_act (${action}): Chrome logado falhou (${res?.reason || 'sem resultado'}).`;
      }
      contextVersion = res.contextVersion;
      const text = textFromMcpResult(res ? res.result : null);
      if (action === 'screenshot' && !text) {
        return 'web_act: screenshot capturado (imagem não retorna como texto — use snapshot para ler a página).';
      }
      if (!text) return `web_act (${action}): ação executada (sem texto de retorno). Rode snapshot para ver o estado atual.`;
      return `web_act (${action}) via Chrome logado:\n\n${cap(text)}`;
    } catch (e) {
      return `Erro web_act: ${(e && e.message) || 'falha ao agir na web'}`;
    }
  }

  return { def: WEB_ACT_DEF, execute, mutating: true };
}
